/**
 * Messaging config — the ONE per-shop control center for every outbound message SLIK sends: appointment
 * reminders (incl. the 1h reminder that carries the self-check-in link), booking confirmations, reward
 * reveals, no-show/cancellation notices, and the pull-forward / waitlist growth nudges. Each message type
 * has its own on/off, channel(s), timing, and editable template. Persisted at `shopSettings/messaging`,
 * edited in Settings → Messaging, and read at runtime by every sender (the reminder scheduler CF, the
 * appointment notify service, the offer/waitlist publishers). Pure + unit-tested; the Cloud Functions read
 * the SAME doc shape (keep field names in sync). BEHAVIOR-PIN: CADENCE-LOYALTY-ENGINE-SPEC.md → Messaging.
 */

export type MsgChannel = 'sms' | 'email' | 'portal';

export type MsgType =
  | 'reminder24h' | 'reminder1h' | 'confirmation' | 'rewardEarned'
  | 'noShow' | 'cancellation' | 'pullForwardOffer' | 'waitlistOpened';

export interface MessageSpec {
  enabled: boolean;
  channels: MsgChannel[];   // which rails this message goes out on (intersected with master toggles)
  template: string;         // copy with {firstName} {barber} {time} {date} {service} {checkinLink} {bookLink} {coins} {daysEarlier} {senderName}
  leadMinutes?: number;     // for reminders: how many minutes BEFORE the appointment to send
}

export interface MessagingConfig {
  // Master channel switches (SMS also gated on toll-free approval elsewhere).
  smsEnabled: boolean;
  emailEnabled: boolean;
  portalEnabled: boolean;
  senderName: string;                 // shop name used in copy + SMS footer
  // Guardrails.
  quietStartMin: number;              // no SMS at/after this minute-of-day …
  quietEndMin: number;                // … until this minute-of-day (overnight window)
  maxPerDayPerClient: number;         // cap outbound messages per client per day (0 = no cap)
  appendStopFooter: boolean;          // append "Reply STOP to opt out" to SMS (compliance)
  messages: Record<MsgType, MessageSpec>;
}

const M = (enabled: boolean, channels: MsgChannel[], template: string, leadMinutes?: number): MessageSpec =>
  ({ enabled, channels, template, ...(leadMinutes != null ? { leadMinutes } : {}) });

export const DEFAULT_MESSAGING_CONFIG: MessagingConfig = {
  smsEnabled: false,     // off until the toll-free number clears carrier verification
  emailEnabled: true,
  portalEnabled: true,
  senderName: 'SLIK Barber Lounge',
  quietStartMin: 21 * 60, // 9:00 PM
  quietEndMin: 8 * 60,    // 8:00 AM
  maxPerDayPerClient: 3,
  appendStopFooter: true,
  messages: {
    reminder24h:      M(true,  ['email', 'portal'], 'Reminder: your appointment with {barber} is tomorrow at {time}. See you then! — {senderName}', 1440),
    reminder1h:       M(true,  ['email', 'portal'], 'Your appointment with {barber} is in about an hour ({time}). When you arrive, tap here to check in: {checkinLink}', 60),
    confirmation:     M(true,  ['email', 'portal'], "You're booked, {firstName}! {barber} on {date} at {time}. See you soon. ✂️"),
    rewardEarned:     M(true,  ['portal'],          'Nice! You earned {coins} coins for coming in early — they\'re in your wallet 🎉'),
    noShow:           M(true,  ['email'],           'We missed you today, {firstName}. Whenever you\'re ready, we\'d love to get you back in — just book anytime.'),
    cancellation:     M(true,  ['email', 'portal'], 'Your appointment with {barber} on {date} at {time} has been cancelled.'),
    pullForwardOffer: M(true,  ['portal'],          '{firstName}, a spot with {barber} opened {daysEarlier} days sooner than usual — book it and earn {coins} coins when you come in: {bookLink}'),
    waitlistOpened:   M(true,  ['portal'],          'Good news {firstName} — a spot with {barber} just opened at {time}. Grab it before it\'s gone: {bookLink}'),
  },
};

const MSG_TYPES: MsgType[] = [
  'reminder24h', 'reminder1h', 'confirmation', 'rewardEarned',
  'noShow', 'cancellation', 'pullForwardOffer', 'waitlistOpened',
];
const CHANNELS: MsgChannel[] = ['sms', 'email', 'portal'];

/** Merge a stored (possibly partial/legacy) config over the defaults, guarding types per-message. */
export function mergeMessagingConfig(partial?: Partial<MessagingConfig> | null): MessagingConfig {
  const p = partial || {};
  const d = DEFAULT_MESSAGING_CONFIG;
  const numOr = (v: any, fb: number) => { const n = Number(v); return Number.isFinite(n) ? n : fb; };
  const out: MessagingConfig = {
    smsEnabled: p.smsEnabled === true,
    emailEnabled: p.emailEnabled !== false,
    portalEnabled: p.portalEnabled !== false,
    senderName: typeof p.senderName === 'string' && p.senderName.trim() ? p.senderName : d.senderName,
    quietStartMin: numOr(p.quietStartMin, d.quietStartMin),
    quietEndMin: numOr(p.quietEndMin, d.quietEndMin),
    maxPerDayPerClient: Math.max(0, numOr(p.maxPerDayPerClient, d.maxPerDayPerClient)),
    appendStopFooter: p.appendStopFooter !== false,
    messages: {} as Record<MsgType, MessageSpec>,
  };
  const pm = (p.messages || {}) as Partial<Record<MsgType, Partial<MessageSpec>>>;
  for (const t of MSG_TYPES) {
    const dm = d.messages[t];
    const src = pm[t] || {};
    const channels = Array.isArray(src.channels) ? src.channels.filter((c): c is MsgChannel => CHANNELS.includes(c as MsgChannel)) : dm.channels;
    out.messages[t] = {
      enabled: src.enabled !== false,
      channels: channels.length ? channels : dm.channels,
      template: typeof src.template === 'string' && src.template.trim() ? src.template : dm.template,
      ...(dm.leadMinutes != null ? { leadMinutes: numOr(src.leadMinutes, dm.leadMinutes) } : {}),
    };
  }
  return out;
}

/** Fill {placeholders} from vars; unknown placeholders are left as-is (visible, so a typo is obvious). */
export function renderTemplate(tpl: string, vars: Record<string, string | number | undefined>): string {
  return (tpl || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}

/** The channels a message will ACTUALLY go out on = its channels ∩ the master-enabled channels. */
export function resolveChannels(cfg: MessagingConfig, type: MsgType): MsgChannel[] {
  const spec = cfg.messages[type];
  if (!spec || !spec.enabled) return [];
  const masterOn = (c: MsgChannel) => c === 'sms' ? cfg.smsEnabled : c === 'email' ? cfg.emailEnabled : cfg.portalEnabled;
  return spec.channels.filter(masterOn);
}

/** Is a minute-of-day inside the (possibly overnight) quiet window? Used to hold SMS, not email/portal. */
export function isQuietHour(minuteOfDay: number, cfg: MessagingConfig): boolean {
  const { quietStartMin: s, quietEndMin: e } = cfg;
  if (s === e) return false;
  return s < e ? (minuteOfDay >= s && minuteOfDay < e) : (minuteOfDay >= s || minuteOfDay < e);
}
